import { GenericTable } from "@visiblethread/react-components";
import { modalTypes } from "constants/types";
import { array, bool, func, shape, string } from "prop-types";
import { useCallback, useMemo, useState } from "react";

import classes from "./HeadingSyncModal.module.scss";
import {
  buildDeleteColumns,
  buildDeleteRows,
  buildSyncColumns,
  buildSyncRows,
} from "./HeadingSyncModalColumns";
import { HeadingSyncModalShell } from "./HeadingSyncModalComponents";
import {
  buildHierarchyIndex,
  getDescendants,
  isRowEnabled,
} from "./HeadingSyncModalHierarchy";

const KEEP_INTRO =
  'The sections below exist in this file. If you want to retain them in your outline, check the "keep" box.';
const SYNC_INTRO =
  "The following changes will be synced to the proposal outline:";
const SCORING_NOTE =
  "Note: any scoring analysis requires a save of the current document";
const ORDER_CHANGED_MESSAGE = "Section order has changed.";

const HeadingSyncModal = ({
  headerText = "Heading changes detected",
  html,
  headings = {},
  onConfirmSync,
  onClose,
  showScoringNote = false,
  submitText = "Save",
}) => {
  const rows = useMemo(() => buildSyncRows(headings), [headings]);
  const { parentOf } = useMemo(() => buildHierarchyIndex(rows), [rows]);
  const columns = useMemo(() => buildSyncColumns({ parentOf }), [parentOf]);

  const onSubmit = () => {
    onConfirmSync(html, headings);
  };

  return (
    <HeadingSyncModalShell
      modalType={modalTypes.HEADING_SYNC_MODAL}
      headerText={headerText}
      submitText={submitText}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      <p>{SYNC_INTRO}</p>

      {showScoringNote && (
        <p data-testid="scoring-note">
          <strong>{SCORING_NOTE}</strong>
        </p>
      )}

      {rows.length > 0 && (
        <div className={classes["heading_sync_modal__table"]}>
          <GenericTable data={rows} columns={columns} />
        </div>
      )}
      {headings.orderChanged && (
        <p className={classes["heading_sync_modal__order_note"]}>
          {ORDER_CHANGED_MESSAGE}
        </p>
      )}
    </HeadingSyncModalShell>
  );
};

HeadingSyncModal.propTypes = {
  html: string,
  headerText: string,
  headings: shape({
    titleChanges: array,
    levelChanges: array,
    deletions: array,
    additions: array,
    orderChanged: bool,
  }),
  onClose: func,
  onConfirmSync: func,
  showScoringNote: bool,
  submitText: string,
};

export const HeadingDeleteModal = ({
  headerText = "Heading changes detected",
  html,
  headings = {},
  onConfirmSync,
  onClose,
  submitText = "Save",
}) => {
  const [keptIds, setKeptIds] = useState(() => new Set());

  const rows = useMemo(
    () => buildDeleteRows(headings.deletions),
    [headings.deletions]
  );

  const { parentOf, childrenOf } = useMemo(
    () => buildHierarchyIndex(rows),
    [rows]
  );

  const toggleKept = useCallback(
    (outlineItemId) => {
      setKeptIds((prev) => {
        const next = new Set(prev);
        const descendants = getDescendants(outlineItemId, childrenOf);
        if (next.has(outlineItemId)) {
          next.delete(outlineItemId);
          for (const id of descendants) {
            next.delete(id);
          }
        } else {
          next.add(outlineItemId);
          for (const id of descendants) {
            next.add(id);
          }
        }
        return next;
      });
    },
    [childrenOf]
  );

  const isEnabled = useCallback(
    (outlineItemId) => isRowEnabled(outlineItemId, parentOf, keptIds),
    [parentOf, keptIds]
  );

  const columns = useMemo(
    () =>
      buildDeleteColumns({
        keptIds,
        onToggle: toggleKept,
        parentOf,
        isEnabled,
      }),
    [keptIds, toggleKept, parentOf, isEnabled]
  );

  const onSubmit = () => {
    onConfirmSync(html, headings, Array.from(keptIds));
  };

  return (
    <HeadingSyncModalShell
      modalType={modalTypes.HEADING_DELETE_MODAL}
      headerText={headerText}
      submitText={submitText}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      <p>{KEEP_INTRO}</p>
      <div className={classes["heading_sync_modal__table"]}>
        <GenericTable data={rows} columns={columns} />
      </div>
    </HeadingSyncModalShell>
  );
};

HeadingDeleteModal.propTypes = {
  html: string,
  headerText: string,
  headings: shape({
    deletions: array,
  }),
  onClose: func,
  onConfirmSync: func,
  submitText: string,
};

export default HeadingSyncModal;
